import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  makeSelectAuth,
  makeSelectProfile,
  makeSelectSubscriptionStatus,
} from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import {
  updateSubscriptionStatus,
  getSubscriptionStatus,
} from 'containers/Auth/actions';
import useIsAuthPath from 'components/Hooks/useIsAuthPath';
import useIsEmailVerified from 'components/Hooks/useIsEmailVerified';
import EditEmailModal from 'containers/Account/EditEmailModal';
import _get from 'lodash/get';

/**
 * Hook to force existing users to verify their personal email
 *
 * This hook:
 * - Detects when a user has an unverified personal email
 * - Fetches the user's current subscription preferences via Redux
 * - Renders a blocking modal that forces email verification
 * - Preserves subscription preferences when updating email
 *
 * Use this when:
 * - User has personal email but it's not verified
 * - Email verification is enabled (isEVEnabled)
 * - You need to block/force verification before user can proceed
 * - You want to preserve subscription preferences during verification
 *
 * @returns {Object} { shouldShowEditModal, markAsShown, renderModal, isLoading }
 */
const useForcePersonalEmailVerification = () => {
  const dispatch = useDispatch();
  const auth = useSelector(makeSelectAuth());
  const profile = useSelector(makeSelectProfile());
  const clientDetails = useSelector(makeSelectClientDetails());
  const subscriptionStatus = useSelector(makeSelectSubscriptionStatus());
  const isAuthPath = useIsAuthPath();
  const {
    isEnabled: isEVEnabled,
    verified: isWorkEmailVerified,
  } = useIsEmailVerified();

  const [hasShownModal, setHasShownModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Don't show personal email prompt if work email verification is required but not yet verified
  const isWorkEmailVerificationPending = isEVEnabled && !isWorkEmailVerified;

  const personalEmail = profile?.personalEmail;

  const shouldShowEditModal =
    clientDetails?.metadata?.requirePersonalEmail &&
    personalEmail &&
    !profile?.personalEmailVerified &&
    isEVEnabled && // Only force verification if EV is currently enabled
    !isWorkEmailVerificationPending && // Wait for work email to be verified first
    auth?.isLoaded &&
    !auth?.isEmpty &&
    !isAuthPath &&
    !hasShownModal; // Only show once per login

  // Reset shown modal on logout
  useEffect(() => {
    if (auth?.isEmpty) {
      setHasShownModal(false);
    }
  }, [auth?.isEmpty]);

  // Fetch subscription status when user needs to verify
  useEffect(() => {
    if (shouldShowEditModal) {
      dispatch(getSubscriptionStatus(personalEmail));
    }
  }, [shouldShowEditModal]);

  // Open modal when subscription status is fetched
  useEffect(() => {
    if (shouldShowEditModal && subscriptionStatus?.email) {
      setIsModalOpen(true);
    }
  }, [shouldShowEditModal, subscriptionStatus]);

  // Get subscription array from status
  const subscription =
    subscriptionStatus?.status !== 'subscribed'
      ? []
      : subscriptionStatus?.subscription || [];

  // Handle save: dispatch Redux action with preserved subscription
  const handleSave = async (newEmail, hideModal = true) => {
    dispatch(
      updateSubscriptionStatus({
        email: newEmail,
        subscription,
        isPersonalEmail: true,
        skipSubscriptionUpdate: false,
        refetchSubscription: true,
        oldEmail: personalEmail,
      }),
    );

    if (hideModal) {
      setIsModalOpen(false);
      setHasShownModal(true);
    }
  };

  // Handle close: mark as shown
  const handleClose = () => {
    setIsModalOpen(false);
    setHasShownModal(true);
  };

  // Render modal function
  const renderModal = () => {
    // Don't render until subscription is fetched
    if (!isModalOpen || !subscriptionStatus) return null;

    return (
      <EditEmailModal
        open="personal"
        onClose={handleClose}
        onSave={handleSave}
        initialEmail={profile?.personalEmail || ''}
        workEmailDomains={_get(
          clientDetails,
          'context.requiredEmailDomains',
          [],
        )}
        currentWorkEmail={profile?.email || ''}
        currentPersonalEmail={profile?.personalEmail || ''}
        isForceVerification
      />
    );
  };

  const markAsShown = () => {
    setHasShownModal(true);
  };

  return {
    shouldShowEditModal,
    markAsShown,
    renderModal,
    isLoading: shouldShowEditModal && !subscriptionStatus,
  };
};

export default useForcePersonalEmailVerification;
